import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Grid from '@mui/material/Grid';

interface Trend {
  pct: number;
  isUp: boolean;
}

interface Tile {
  id: string;
  label: string;
  kpiKey: string;
  filterField: string | null;
  filterValue: string | null;
  color: TypographyProps['color'];
  hideTrend?: boolean;
  tooltip: string;
  format?: (value: number) => string;
}

interface TileGroup {
  label: string;
  tiles: Tile[];
}

interface AggregateKPIData {
  previous?: Record<string, number>;
  [key: string]: unknown;
}

function calcTrend(current: number, previous?: number): Trend | null {
  if (!previous || previous === 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  return { pct, isUp: pct >= 0 };
}

const TILE_GROUPS: TileGroup[] = [
  {
    label: 'Stock',
    tiles: [
      { id: 'totalInStock', label: 'Total In Stock', kpiKey: 'totalInStock', filterField: null, filterValue: null, color: 'text.primary', hideTrend: true, tooltip: 'Units currently received and available in the warehouse (not allocated to any order).' },
      { id: 'totalInProduction', label: 'Total In Production', kpiKey: 'totalInProduction', filterField: null, filterValue: null, color: 'text.primary', hideTrend: true, tooltip: 'Units ordered from production but not yet received.' },
      { id: 'overallSellThroughRate', label: 'Sell-Through Rate', kpiKey: 'overallSellThroughRate', filterField: null, filterValue: null, color: 'text.primary', format: (v) => `${v}%`, tooltip: 'Percentage of available inventory sold in the selected period. Calculated as: units sold ÷ (units sold + in stock).' },
    ],
  },
  {
    label: 'Movement',
    tiles: [
      { id: 'fastMovingCount', label: 'Fast Moving', kpiKey: 'fastMovingCount', filterField: 'movementCategory', filterValue: 'fast', color: 'success.main', tooltip: 'Products with a sell-through rate above 50% — selling quickly relative to available stock. Click to filter the table.' },
      { id: 'normalMovingCount', label: 'Normal Moving', kpiKey: 'normalMovingCount', filterField: 'movementCategory', filterValue: 'normal', color: 'warning.main', tooltip: 'Products with a sell-through rate between 10% and 50% — moving at a steady pace. Click to filter the table.' },
      { id: 'slowMovingCount', label: 'Slow Moving', kpiKey: 'slowMovingCount', filterField: 'movementCategory', filterValue: 'slow', color: 'error.main', tooltip: 'Products with a sell-through rate below 10% — at risk of becoming dead stock. Click to filter the table.' },
    ],
  },
  {
    label: 'Forecast Status',
    tiles: [
      { id: 'reorderSoonCount', label: 'Reorder Soon', kpiKey: 'reorderSoonCount', filterField: 'stockStatus', filterValue: 'reorderSoon', color: 'error.main', tooltip: 'Products with less than 2 weeks of supply remaining at the current sales velocity. Consider reordering. Click to filter the table.' },
      { id: 'overstockedCount', label: 'Overstocked', kpiKey: 'overstockedCount', filterField: 'stockStatus', filterValue: 'overstocked', color: 'warning.main', tooltip: 'Products with available supply exceeding 3× the projected demand for the selected period. Click to filter the table.' },
      { id: 'onTrackCount', label: 'On Track', kpiKey: 'onTrackCount', filterField: 'stockStatus', filterValue: 'onTrack', color: 'success.main', tooltip: 'Products with a healthy supply level — enough stock to meet projected demand without excess. Click to filter the table.' },
    ],
  },
];

interface KPICardProps {
  tile: Tile;
  current: number;
  previous?: number | undefined;
  isActive: boolean;
  onClick?: () => void;
}

function KPICard({
  tile, current, previous, isActive, onClick,
}: KPICardProps) {
  const format = tile.format || ((v: number) => String(v));
  const trend = tile.hideTrend ? null : calcTrend(current, previous);
  const isClickable = tile.filterField !== null;

  return (
    <Tooltip title={tile.tooltip} placement="top" arrow>
      <Card
        variant="outlined"
        onClick={isClickable ? onClick : undefined}
        sx={{
          height: '100%',
          cursor: isClickable ? 'pointer' : 'default',
          borderColor: isActive ? 'primary.main' : undefined,
          borderWidth: isActive ? 2 : 1,
          backgroundColor: isActive ? 'action.selected' : undefined,
          '&:hover': isClickable ? { borderColor: 'primary.light', backgroundColor: 'action.hover' } : undefined,
        }}
      >
        <CardContent sx={{ pb: '12px !important' }}>
          <Typography
            variant="body2"
            gutterBottom
            sx={{
              color: 'text.secondary',
            }}
          >
            {tile.label}
          </Typography>
          <Typography variant="h6" color={tile.color}>
            {format(current)}
          </Typography>
          {trend && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5,
            }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                prev:
                {' '}
                {previous !== undefined && format(previous)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', color: trend.isUp ? 'success.main' : 'error.main' }}
              >
                {trend.isUp ? '▲' : '▼'}
                {' '}
                {trend.isUp ? '+' : ''}
                {trend.pct}
                %
              </Typography>
            </Box>
          )}
          {!trend && !tile.hideTrend && previous !== undefined && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 0.5,
              }}
            >
              prev:
              {' '}
              {format(previous)}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Tooltip>
  );
}

interface AggregateKPIsProps {
  kpis?: AggregateKPIData | null | undefined;
  activeTile: string | null;
  onTileClick?: (tile: Tile) => void;
}

export default function AggregateKPIs({ kpis, activeTile, onTileClick }: AggregateKPIsProps) {
  if (!kpis) return null;

  const prev = kpis.previous || {};

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        All Collections Summary
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {TILE_GROUPS.map((group) => (
          <Box key={group.label} sx={{
            border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
          }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 1.5,
                py: 0.5,
                fontWeight: 'bold',
                color: 'text.secondary',
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              {group.label}
            </Typography>
            <Grid container>
              {group.tiles.map((tile, ti) => (
                <Grid
                  key={tile.id}
                  sx={{
                    borderRight: ti < group.tiles.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                  }}
                  size={4}
                >
                  <KPICard
                    tile={tile}
                    current={(kpis[tile.kpiKey] as number) ?? 0}
                    previous={prev[tile.kpiKey]}
                    isActive={activeTile === tile.id}
                    onClick={() => { if (onTileClick) onTileClick(tile); }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
